import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient, { withApiPath } from '../../utils/apiClient';

const sessionId = () => {
  const key = 'freightlogic-workspace-session';
  let value = sessionStorage.getItem(key);
  if (!value) {
    value = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    sessionStorage.setItem(key, value);
  }
  return value;
};

const without = (state, key) => {
  const next = { ...state };
  delete next[key];
  return next;
};

const sanitizeFailure = (error, boundary) => {
  const data = error?.response?.data;
  const status = error?.response?.status || null;
  const details = {};
  const safeFields = new Set([
    'stop_type', 'sequence', 'scheduled_date', 'location_id', 'window_start', 'window_end',
    'appointment_type', 'appointment_required', 'appointment_number', 'reference_number', 'notes',
    'base_revision', 'non_field_errors',
  ]);
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    Object.entries(data).forEach(([field, value]) => {
      if (!safeFields.has(field)) return;
      if (Array.isArray(value)) details[field] = value.map(String).join(', ');
      else if (typeof value === 'string') details[field] = value;
    });
  }
  return {
    boundary,
    status,
    code: data?.code || null,
    currentRevision: data?.current_revision || null,
    details,
  };
};

const boundaryFor = (path, payload) => {
  if (path.includes('load-stops/')) {
    const id = path.match(/load-stops\/(\d+)/)?.[1];
    return id ? `stop:${id}` : `stop:new:${payload?.sequence || 'pending'}`;
  }
  if (path.includes('remarks')) return 'remarks';
  const keys = Object.keys(payload || {});
  if (keys.some((key) => key.startsWith('dispatch_'))) return 'dispatcher';
  if (keys.some((key) => key.startsWith('driver_') || ['truck_number', 'trailer_number'].includes(key))) return 'driver';
  if (keys.some((key) => key.startsWith('equipment_') || key.startsWith('reefer_'))) return 'equipment';
  if (keys.some((key) => ['bill_rate', 'customer_charges'].includes(key))) return 'customerBilling';
  return keys[0] || 'load';
};

export const snapshotBoundaries = (load = {}) => {
  const stops = Array.isArray(load.stops) ? [...load.stops].sort((a, b) => a.sequence - b.sequence) : [];
  const result = {
    status: load.status,
    customer: load.customer_id ?? load.customer ?? null,
    carrier: load.carrier?.id ?? load.carrier_id ?? null,
    dispatcher: { dispatch_name: load.dispatch_name, dispatch_phone: load.dispatch_phone, dispatch_extension: load.dispatch_extension, dispatch_email: load.dispatch_email },
    driver: { driver_name: load.driver_name, driver_phone: load.driver_phone, driver_vin: load.driver_vin, truck_number: load.truck_number, trailer_number: load.trailer_number },
    references: load.references || [],
    equipment: { equipment_type: load.equipment_type, equipment_requirements: load.equipment_requirements || [], trailer_length: load.trailer_length },
    reefer: { reefer_temperature: load.reefer_temperature, is_fresh: load.is_fresh, is_frozen: load.is_frozen, special_load_requirements: load.special_load_requirements || {} },
    customerBilling: { customer_charges: load.customer_charges || [], bill_rate: load.bill_rate },
    stopOrder: stops.map((stop) => stop.id),
  };
  stops.forEach((stop) => {
    result[`stop:${stop.id}`] = {
      id: stop.id, stop_type: stop.stop_type, sequence: stop.sequence, scheduled_date: stop.scheduled_date,
      location_id: stop.location?.id ?? null, window_start: stop.window_start, window_end: stop.window_end,
      appointment_type: stop.appointment_type, appointment_number: stop.appointment_number || '',
      reference_number: stop.reference_number || '', notes: stop.notes || ''
    };
  });
  return result;
};

export const deriveSaveState = ({ dirty, queued, inFlight, failed, conflicts }) => {
  if (Object.keys(conflicts).length) return 'Conflict';
  if (Object.keys(failed).length) return 'Save failed';
  if (Object.keys(queued).length || Object.keys(inFlight).length) return 'Saving…';
  if (Object.keys(dirty).length) return 'Unsaved';
  return 'Saved';
};

export const reconcileBoundary = (base, local, remote) => {
  const equal = (left, right) => JSON.stringify(left) === JSON.stringify(right);
  const localChanged = !equal(local, base);
  const remoteChanged = !equal(remote, base);
  if (!localChanged && remoteChanged) return { value: remote, acknowledged: remote };
  if (localChanged && !remoteChanged) return { value: local, dirty: true, acknowledged: base };
  if (localChanged && remoteChanged && equal(local, remote)) return { value: remote, acknowledged: remote };
  if (localChanged && remoteChanged) return { value: local, dirty: true, acknowledged: base, conflict: { local, remote } };
  return { value: base, acknowledged: base };
};

export const shouldAutoSync = ({ viewerCount, unseenChanges, syncing, conflict }) => (
  viewerCount !== null && viewerCount <= 1 && unseenChanges > 0 && !syncing && !conflict
);

export const shouldShowManualSync = ({ viewerCount, unseenChanges, conflict }) => (
  unseenChanges > 0 && (viewerCount > 1 || Boolean(conflict))
);

export default function useLoadWorkspaceSync(loadId, initialLoad, onSnapshot) {
  const [revision, setRevision] = useState(initialLoad?.revision || 1);
  const revisionRef = useRef(revision);
  const [boundaries, setBoundaries] = useState({ dirty: {}, queued: {}, inFlight: {}, failed: {}, conflicts: {}, acknowledged: {} });
  const boundariesRef = useRef(boundaries);
  const retryByBoundary = useRef({});
  const mutationGeneration = useRef({});
  const mutationQueue = useRef(Promise.resolve());
  const baseSnapshot = useRef(initialLoad || {});
  const flushers = useRef(new Set());
  const [viewerCount, setViewerCount] = useState(null);
  const [unseenChanges, setUnseenChanges] = useState(0);
  const [reconciling, setReconciling] = useState(false);
  const reconcilingRef = useRef(false);
  const id = useMemo(sessionId, []);
  const update = useCallback((fn) => setBoundaries((current) => fn(current)), []);
  useEffect(() => { boundariesRef.current = boundaries; }, [boundaries]);

  useEffect(() => { revisionRef.current = revision; }, [revision]);
  useEffect(() => {
    if (initialLoad?.revision) {
      setRevision(initialLoad.revision);
      revisionRef.current = initialLoad.revision;
      baseSnapshot.current = initialLoad;
    }
  }, [initialLoad?.revision]);

  const markDirty = useCallback((boundary, draft = true) => update((s) => ({ ...s, dirty: { ...s.dirty, [boundary]: draft } })), [update]);
  const clearDirty = useCallback((boundary) => update((s) => ({ ...s, dirty: without(s.dirty, boundary) })), [update]);

  const mutation = useCallback((method, path, payload = {}, explicitBoundary) => {
    const boundary = explicitBoundary || boundaryFor(path, payload);
    const generation = (mutationGeneration.current[boundary] || 0) + 1;
    mutationGeneration.current[boundary] = generation;
    markDirty(boundary, payload);
    update((s) => ({ ...s, queued: { ...s.queued, [boundary]: payload }, failed: without(s.failed, boundary) }));
    const execute = async () => {
      update((s) => ({ ...s,
        queued: mutationGeneration.current[boundary] === generation ? without(s.queued, boundary) : s.queued,
        inFlight: { ...s.inFlight, [boundary]: payload } }));
      try {
        const response = await apiClient.request({ method, url: withApiPath(path), data: { ...payload, base_revision: revisionRef.current } });
        const nextRevision = response.data?.load_revision || response.data?.revision;
        if (nextRevision) {
          revisionRef.current = nextRevision;
          setRevision(nextRevision);
          setUnseenChanges(0);
        }
        // Only this boundary is acknowledged; unrelated drafts/errors survive.
        const isLatest = mutationGeneration.current[boundary] === generation;
        update((s) => ({ ...s, dirty: isLatest ? without(s.dirty, boundary) : s.dirty,
          inFlight: isLatest ? without(s.inFlight, boundary) : s.inFlight,
          failed: without(s.failed, boundary), conflicts: without(s.conflicts, boundary),
          acknowledged: { ...s.acknowledged, [boundary]: response.data } }));
        if (isLatest) delete retryByBoundary.current[boundary];
        return response;
      } catch (error) {
        if (mutationGeneration.current[boundary] !== generation) throw error;
        update((s) => ({ ...s, inFlight: without(s.inFlight, boundary),
          ...(error.response?.status === 409
            ? { conflicts: { ...s.conflicts, [boundary]: { ...error.response.data, diagnostic: sanitizeFailure(error, boundary) } } }
            : { failed: { ...s.failed, [boundary]: { error, draft: payload, diagnostic: sanitizeFailure(error, boundary) } } }) }));
        throw error;
      }
    };
    retryByBoundary.current[boundary] = execute;
    const queued = mutationQueue.current.catch(() => {}).then(execute);
    mutationQueue.current = queued;
    return queued;
  }, [markDirty, update]);

  const hasNavigationRisk = Object.values(boundaries).slice(0, 5).some((registry) => Object.keys(registry).length > 0);
  const refresh = useCallback(async () => {
    if (!loadId) return;
    if (reconcilingRef.current) return;
    reconcilingRef.current = true;
    setReconciling(true);
    try {
      const response = await apiClient.get(withApiPath(`/loads/${loadId}/`));
      const base = snapshotBoundaries(baseSnapshot.current);
      const remote = snapshotBoundaries(response.data);
      const current = boundariesRef.current;
      let dirty = { ...current.dirty };
      let conflicts = { ...current.conflicts };
      Object.keys({ ...base, ...remote, ...dirty }).forEach((boundary) => {
        const local = Object.prototype.hasOwnProperty.call(dirty, boundary) ? dirty[boundary] : base[boundary];
        const result = reconcileBoundary(base[boundary], local, remote[boundary]);
        if (result.conflict) conflicts[boundary] = result.conflict;
        else delete conflicts[boundary];
        if (!result.dirty) delete dirty[boundary];
      });
      update((s) => ({ ...s, dirty, conflicts }));
      if (Object.keys(conflicts).length) return response;
      baseSnapshot.current = response.data;
      if (!Object.keys(dirty).length) onSnapshot(response.data);
      revisionRef.current = response.data.revision;
      setRevision(response.data.revision);
      setUnseenChanges(0);
      return response;
    } finally {
      reconcilingRef.current = false;
      setReconciling(false);
    }
  }, [loadId, onSnapshot, update]);

  const registerFlusher = useCallback((flusher) => {
    flushers.current.add(flusher);
    return () => flushers.current.delete(flusher);
  }, []);
  const flushAndDrain = useCallback(async () => {
    await Promise.all([...flushers.current].map((flush) => flush()));
    await mutationQueue.current.catch(() => {});
  }, []);
  const navigationRiskNow = useCallback(() => Object.values(boundariesRef.current).slice(0, 5).some((registry) => Object.keys(registry).length), []);

  useEffect(() => {
    if (!loadId) return undefined;
    let active = true;
    const heartbeat = async () => {
      try {
        const response = await apiClient.post(withApiPath(`/loads/${loadId}/presence/`), { session_id: id });
        if (!active) return;
        setViewerCount(response.data.viewer_count);
        const requestedRevision = revisionRef.current;
        const statusResponse = await apiClient.get(withApiPath(`/loads/${loadId}/sync-status/`), { params: { after_revision: requestedRevision } });
        // A save or reconciliation may have advanced the incorporated revision
        // while this request was in flight. Never let that stale response
        // resurrect events acknowledged by the newer revision.
        if (active && revisionRef.current === requestedRevision) {
          setUnseenChanges(statusResponse.data.unseen_change_count);
        }
      } catch (_) { if (active) setViewerCount(null); }
    };
    heartbeat();
    const timer = setInterval(heartbeat, 15000);
    return () => { active = false; clearInterval(timer); apiClient.delete(withApiPath(`/loads/${loadId}/presence/`), { data: { session_id: id } }).catch(() => {}); };
  }, [id, loadId]);

  useEffect(() => {
    if (shouldAutoSync({ viewerCount, unseenChanges, syncing: reconciling, conflict: Object.keys(boundaries.conflicts).length })) {
      refresh().catch(() => {});
    }
  }, [boundaries.conflicts, reconciling, refresh, unseenChanges, viewerCount]);

  useEffect(() => {
    if (!hasNavigationRisk) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasNavigationRisk]);

  const saveState = deriveSaveState(boundaries);
  const retryFailed = useCallback(async () => {
    const failed = Object.keys(boundariesRef.current.failed);
    for (const boundary of failed) {
      await retryByBoundary.current[boundary]?.().catch(() => {});
    }
  }, []);
  const resolveConflict = useCallback(async (boundary) => {
    await refresh();
    return retryByBoundary.current[boundary]?.();
  }, [refresh]);
  return {
    revision, viewerCount, unseenChanges, reconciling, boundaries, saveState, hasNavigationRisk,
    pending: Object.keys(boundaries.queued).length + Object.keys(boundaries.inFlight).length,
    failure: Object.values(boundaries.failed)[0]?.error || null,
    failedBoundaries: Object.keys(boundaries.failed),
    failureDiagnostic: Object.values(boundaries.failed)[0]?.diagnostic || null,
    conflictBoundaries: Object.keys(boundaries.conflicts),
    conflictDiagnostic: Object.values(boundaries.conflicts)[0]?.diagnostic || null,
    conflict: Object.values(boundaries.conflicts)[0] || null,
    markDirty, clearDirty, registerFlusher, flushAndDrain, navigationRiskNow,
    patchLoad: (payload, boundary) => mutation('patch', `/loads/${loadId}/`, payload, boundary),
    createRemark: (payload) => mutation('post', `/loads/${loadId}/remarks/`, payload, 'remarks'),
    createStop: (payload) => mutation('post', '/load-stops/', { ...payload, load: loadId }),
    patchStop: (stopId, payload) => mutation('patch', `/load-stops/${stopId}/`, payload, `stop:${stopId}`),
    deleteStop: (stopId) => mutation('delete', `/load-stops/${stopId}/`, {}, `stop:${stopId}`),
    reorderStops: (orderedStopIds) => mutation('post', `/loads/${loadId}/reorder-stops/`, { ordered_stop_ids: orderedStopIds }, 'stopOrder'),
    retry: (boundary) => retryByBoundary.current[boundary]?.(), retryFailed, resolveConflict,
    refresh,
  };
}
